import React from 'react';
import { motion } from 'framer-motion';
import { Gift, Sprout, Heart, Apple } from 'lucide-react';

const steps = [
  {
    num: '01',
    title: 'Gift',
    Icon: Gift,
    copy: 'Choose a tree and dedicate it. Add a name, an occasion, a message that will outlive seasons.',
  },
  {
    num: '02',
    title: 'Plant',
    Icon: Sprout,
    copy: 'Planted by hand in the Pallavit Orchard with a wooden name plaque that bears your dedication.',
  },
  {
    num: '03',
    title: 'Nurture',
    Icon: Heart,
    copy: 'Our farmers care for it year-round. You receive seasonal photos and gentle growth updates.',
  },
  {
    num: '04',
    title: 'Harvest',
    Icon: Apple,
    copy: 'Fresh fruit delivered to your loved one every season — a living gift that gives, for 15–20 years.',
  },
];

export default function HowItWorks() {
  return (
    <section id="journey" className="relative py-28 md:py-40 bg-brand-bg" data-testid="how-it-works-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-2xl mb-20">
          <p className="text-brand-gold tracking-[0.25em] uppercase text-xs mb-5 font-medium">The Journey</p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-brand-ink leading-[1.05] tracking-tight font-light">
            Four gentle steps, <em className="text-brand-forest">one lifelong gift</em>.
          </h2>
          <p className="mt-6 text-brand-ink2 text-lg max-w-xl leading-relaxed">
            Not a transaction. A quiet ritual — from your hands, through our soil, into someone's life.
          </p>
        </div>

        <div className="relative">
          {/* Connecting line */}
          <div className="hidden md:block absolute top-24 left-[10%] right-[10%] h-px bg-gradient-to-r from-transparent via-brand-gold/50 to-transparent" />

          <div className="grid grid-cols-1 md:grid-cols-4 gap-10 md:gap-6">
            {steps.map((s, i) => (
              <motion.div
                key={s.num}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.9, delay: i * 0.15, ease: [0.22, 1, 0.36, 1] }}
                className="relative"
                data-testid={`journey-step-${i + 1}`}
              >
                <div className="relative z-10 inline-flex items-center justify-center w-20 h-20 rounded-full bg-brand-bg border border-brand-line mb-6">
                  <s.Icon className="w-7 h-7 text-brand-forest" strokeWidth={1.5} />
                </div>
                <div className="font-serif italic text-brand-gold text-sm tracking-widest mb-3">{s.num}</div>
                <h3 className="font-serif text-3xl text-brand-ink mb-3 font-medium">{s.title}</h3>
                <p className="text-brand-ink2 leading-relaxed text-[15px]">{s.copy}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
